import { UserPrefsInput } from 'dtos/users/UserPrefsInput';
import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { createForm } from 'forms/completeProfileForm';
import { Match } from 'navigo';
import { LoadStatus } from 'utils/types';
import { UserOutput } from 'dtos/users/UserOutput';
import { IRouteStore } from 'stores/RouteStore';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { AccountsStore } from 'stores/users/AccountsStore';
import { Source } from 'dtos/users/common';

export type AccountOption = {
  inputValue?: string;
  title: string;
};

export class CompleteProfileStore {
  form = createForm();
  status: LoadStatus = 'none';
  message?: string;

  constructor(
    private readonly authStore: AuthStore,
    private readonly routeStore: IRouteStore,
    private readonly accountsStore: AccountsStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    this.status = 'none';
    this.message = undefined;

    this.accountsStore.ensureAccountsLoaded();
    const { fullName, email, jobRole, companyName } = this.authStore.currentUser || {};

    this.form.set('default', {
      fullName,
      email,
      // eslint-disable-next-line no-restricted-syntax
      jobRole: jobRole ? { title: jobRole } : null,
      // eslint-disable-next-line no-restricted-syntax
      companyName: companyName ? { title: companyName } : null,
    });
    this.form.reset();
  }

  async completeProfile() {
    this.status = 'loading';
    this.message = undefined;
    let dto: UserPrefsInput = {
      profileComplete: true,
    };

    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid) {
      return;
    }
    const { fullName, email, jobRole, companyName, hasCostarLicense } = this.form.values();
    const emailUpdates = this.authStore.currentUser?.source === Source.LOCAL ? {} : { email };
    dto = {
      ...dto,
      fullName,
      ...emailUpdates,
      jobRole: jobRole?.title || '',
      companyName: companyName?.title || '',
      hasCostarLicense,
    };

    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/user/prefs`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });
      if (!response.ok) throw new Error('Error changing profile');
      const user: UserOutput = nullsToUndefined(await response.json());
      this.authStore.updateCurrentUser(user);
      runInAction(() => {
        this.status = 'success';
        this.message = undefined;
      });
      this.routeStore.goHome();
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = 'Unable to change profile. Please try again.';
      });
    }
  }

  get accounts() {
    return this.accountsStore.accounts;
  }
}
